import { getBiasInfo } from '../../util';
import { fetchAllJsonCached } from '../../fetch-all-json';

const bias_classes = {
  extLib: 'bias-liberal-extreme',
  midLib: 'bias-liberal-significant',
  sltLib: 'bias-liberal-minimal',
  neutral: 'bias-mixed',
  sltCon: 'bias-conservative-minimal',
  midCon: 'bias-conservative-significant',
  extCon: 'bias-conservative-extreme'
};

const biasClassNames = Object.values(bias_classes);

async function fetchBiasNumbers () {
  const response = await fetchAllJsonCached();

  return {
    bias_ratings: response.bias_ratings,
    weekly_bias_graph: response.weekly_bias_graph,
    monthly_bias_graph: response.monthly_bias_graph
  };
}

function getDailyAverageFromWeekly (weeklyData) {
  if (!weeklyData || !Array.isArray(weeklyData) || weeklyData.length === 0) {
    return null;
  }
  // Get entries from the last 24 hours
  const now = new Date();
  const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const recentEntries = weeklyData.filter(entry => {
    const entryDate = new Date(entry.summary_date || entry.record_date);
    return entryDate >= oneDayAgo;
  });

  if (recentEntries.length > 0) {
    const sum = recentEntries.reduce((acc, entry) => acc + entry.bias_score, 0);
    return sum / recentEntries.length;
  }

  // If no entries in last 24h, use the most recent entry
  const sorted = [...weeklyData].sort((a, b) => {
    const dateA = new Date(a.summary_date || a.record_date);
    const dateB = new Date(b.summary_date || b.record_date);
    return dateB - dateA;
  });
  return sorted[0].bias_score;
}

function getDailyAverageFromMonthly (monthlyData) {
  if (!monthlyData || !Array.isArray(monthlyData) || monthlyData.length === 0) {
    return null;
  }
  // Use the most recent daily entry
  const sorted = [...monthlyData].sort((a, b) => {
    const dateA = new Date(a.record_date);
    const dateB = new Date(b.record_date);
    return dateB - dateA;
  });
  return sorted[0].bias_score;
}

/**
 *
 */
export function populateBias (target, secondRating, weeklyRating = null, biasClasses, top, right = 0, fallbackRating = null) {
  let number = parseFloat(secondRating);
  let usedFallback = false;

  // If hourly data is unavailable, fall back to daily data
  if (isNaN(number) && fallbackRating !== null) {
    number = parseFloat(fallbackRating);
    usedFallback = true;
  }

  // Update the label to reflect the data source
  if (target.label) {
    target.label.textContent = usedFallback
      ? 'Latest archived reading'
      : 'Latest hourly reading';
  }

  if (isNaN(number)) {
    target.number.innerHTML = 'N/A';
    target.description.innerHTML = 'Data Unavailable';
    for (const className of biasClassNames) {
      target.box.classList.remove(className);
    }
    target.box.classList.add(biasClasses.neutral);
    target.box.style.top = top;
    target.box.style.right = right;
  } else {
    const [level, biasDescption] = getBiasInfo(number);

    target.number.innerHTML = number.toFixed(3);
    target.description.innerHTML = biasDescption;

    for (const className of biasClassNames) {
      target.box.classList.remove(className);
    }

    target.box.classList.add(biasClasses[level]);
    target.box.style.top = top;
    target.box.style.right = right;
  }

  // Weekly - only populate if data is available
  if (weeklyRating !== null && weeklyRating !== undefined) {
    const weeklyLabel = target.weekly.box.querySelector('.bias-type');
    if (weeklyLabel) weeklyLabel.textContent = 'Latest weekly summary';
    const weekly = parseFloat(weeklyRating);

    if (isNaN(weekly)) {
      target.weekly.number.innerHTML = 'N/A';
      target.weekly.description.innerHTML = 'Data Unavailable';
      for (const className of biasClassNames) {
        target.weekly.box.classList.remove(className);
      }
      target.weekly.box.classList.add(biasClasses.neutral);
      target.weekly.box.style.top = top;
      target.weekly.box.style.right = right;
    } else {
      const [weeklyLevel, weeklyBiasDescption] = getBiasInfo(weekly);

      target.weekly.number.innerHTML = weekly.toFixed(3);
      target.weekly.description.innerHTML = weeklyBiasDescption;

      for (const className of biasClassNames) {
        target.weekly.box.classList.remove(className);
      }

      target.weekly.box.classList.add(biasClasses[weeklyLevel]);
      target.weekly.box.style.top = top;
      target.weekly.box.style.right = right;
    }
  }
}

/**
 *
 */
async function biasNumbers () {
  function getBiasBoxLabel (boxId) {
    const box = document.getElementById(boxId);
    return box ? box.querySelector('.bias-type') : null;
  }

  const google = {
    number: document.getElementById('google_bias_number'),
    description: document.getElementById('google_bias_desc'),
    box: document.getElementById('google_bias_box'),
    label: getBiasBoxLabel('google_bias_box'),
    weekly: {
      number: document.getElementById('google_weekly_bias_number'),
      description: document.getElementById('google_weekly_bias_desc'),
      box: document.getElementById('google_weekly_bias_box')
    }
  };

  const bing = {
    number: document.getElementById('bing_bias_number'),
    description: document.getElementById('bing_bias_desc'),
    box: document.getElementById('bing_bias_box'),
    label: getBiasBoxLabel('bing_bias_box'),
    weekly: {
      number: document.getElementById('bing_weekly_bias_number'),
      description: document.getElementById('bing_weekly_bias_desc'),
      box: document.getElementById('bing_weekly_bias_box')
    }
  };

  const yahoo = {
    number: document.getElementById('yahoo_bias_number'),
    description: document.getElementById('yahoo_bias_desc'),
    box: document.getElementById('yahoo_bias_box'),
    label: getBiasBoxLabel('yahoo_bias_box'),
    weekly: {
      number: document.getElementById('yahoo_weekly_bias_number'),
      description: document.getElementById('yahoo_weekly_bias_desc'),
      box: document.getElementById('yahoo_weekly_bias_box')
    }
  };

  const youtube = {
    number: document.getElementById('youtube_bias_number'),
    description: document.getElementById('youtube_bias_desc'),
    box: document.getElementById('youtube_bias_box'),
    label: getBiasBoxLabel('youtube_bias_box'),
    weekly: {
      number: document.getElementById('youtube_weekly_bias_number'),
      description: document.getElementById('youtube_weekly_bias_desc'),
      box: document.getElementById('youtube_weekly_bias_box')
    }
  };

  const twitter = {
    number: document.getElementById('x_bias_number'),
    description: document.getElementById('x_bias_desc'),
    box: document.getElementById('x_bias_box'),
    label: getBiasBoxLabel('x_bias_box'),
    weekly: {
      number: document.getElementById('x_weekly_bias_number'),
      description: document.getElementById('x_weekly_bias_desc'),
      box: document.getElementById('x_weekly_bias_box')
    }
  };

  const reddit = {
    number: document.getElementById('reddit_bias_number'),
    description: document.getElementById('reddit_bias_desc'),
    box: document.getElementById('reddit_bias_box'),
    label: getBiasBoxLabel('reddit_bias_box'),
    weekly: {
      number: document.getElementById('reddit_weekly_bias_number'),
      description: document.getElementById('reddit_weekly_bias_desc'),
      box: document.getElementById('reddit_weekly_bias_box')
    }
  };

  try {
    const { bias_ratings, weekly_bias_graph, monthly_bias_graph } = await fetchBiasNumbers();

    const platformTargets = {
      google,
      bing,
      yahoo,
      youtube,
      twitter,
      reddit
    };

    const populatedPlatforms = new Set();

    for (const key of Object.keys(bias_ratings.platform_bias)) {
      const secondRating = bias_ratings.platform_bias[key].second_rating;

      // Calculate weekly rating if available
      let weeklyRating = null;
      const weekly = weekly_bias_graph.platform_bias && weekly_bias_graph.platform_bias[key];

      if (weekly && Array.isArray(weekly) && weekly.length > 0) {
        const validEntries = weekly.filter(entry => entry.bias_score != null && !isNaN(entry.bias_score));
        if (validEntries.length > 0) {
          weeklyRating = validEntries.reduce((accumulator, current) => {
            return accumulator + current.bias_score;
          }, 0) / validEntries.length;
        }
      } else if (!weekly) {
        console.warn(`Weekly bias data not available for platform: ${key}`);
      }

      // Compute daily fallback from weekly or monthly data when hourly is unavailable
      let fallbackRating = null;
      if (secondRating === null || secondRating === undefined || isNaN(parseFloat(secondRating))) {
        fallbackRating = getDailyAverageFromWeekly(weekly);
        if (fallbackRating === null) {
          const monthly = monthly_bias_graph.platform_bias && monthly_bias_graph.platform_bias[key];
          fallbackRating = getDailyAverageFromMonthly(monthly);
        }
      }

      if (platformTargets[key]) {
        populateBias(platformTargets[key], secondRating, weeklyRating, bias_classes, undefined, 0, fallbackRating);
        populatedPlatforms.add(key);
      }
    }

    // Fallback: populate platforms missing from hourly data using weekly/monthly graphs
    for (const key of Object.keys(platformTargets)) {
      if (populatedPlatforms.has(key)) continue;

      const weekly = weekly_bias_graph.platform_bias && weekly_bias_graph.platform_bias[key];
      let weeklyRating = null;
      if (weekly && Array.isArray(weekly) && weekly.length > 0) {
        const validEntries = weekly.filter(entry => entry.bias_score != null && !isNaN(entry.bias_score));
        if (validEntries.length > 0) {
          weeklyRating = validEntries.reduce((accumulator, current) => {
            return accumulator + current.bias_score;
          }, 0) / validEntries.length;
        }
      }

      let fallbackRating = getDailyAverageFromWeekly(weekly);
      if (fallbackRating === null) {
        const monthly = monthly_bias_graph.platform_bias && monthly_bias_graph.platform_bias[key];
        fallbackRating = getDailyAverageFromMonthly(monthly);
      }

      populateBias(platformTargets[key], null, weeklyRating, bias_classes, undefined, 0, fallbackRating);
    }
  } catch (error) {
    console.error('There was a problem with the fetch operation:', error.message);
  }
}

/**
 *
 */
export async function initCurrentBiasBoxes () {
  await biasNumbers();
  setInterval(async () => {
    await biasNumbers();
  }, 270000);
}
